"use client";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "../utilities/auth";

// Lazy import the highcharts module to avoid SSR issues
let highchartsModule = null;
const getHighchartsModule = async () => {
  if (!highchartsModule) {
    highchartsModule = await import("../components/chart/highcharts");
  }
  return highchartsModule;
};

const HighchartsContext = createContext({
  isReady: false,
  error: null,
  retry: () => {},
  isRetrying: false,
  Highcharts: null,
});

export const useHighcharts = () => useContext(HighchartsContext);

const INIT_TIMEOUT = 30000;
const MAX_RETRIES = 3;

export function HighchartsProvider({ children }) {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [highchartsInstance, setHighchartsInstance] = useState(null);
  const { user } = useAuth();
  const [isRetrying, setIsRetrying] = useState(false);
  const retryCount = useRef(0);
  const initAttempted = useRef(false);

  const initializeHighcharts = useCallback(async () => {
    try {
      setError(null);

      const module = await getHighchartsModule();
      const { Highcharts, ensureHighchartsModules } = module;

      // Already initialized
      if (Highcharts.__modulesInited) {
        setHighchartsInstance(Highcharts);
        setIsReady(true);
        setError(null);
        return;
      }

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error("Highcharts initialization timed out")),
          INIT_TIMEOUT,
        );
      });

      await Promise.race([ensureHighchartsModules(), timeoutPromise]);

      setHighchartsInstance(Highcharts);
      setIsReady(true);
      setError(null);
      retryCount.current = 0;
    } catch (err) {
      console.error("Highcharts initialization failed:", err);
      setError(err.message || "Failed to load charts");

      if (retryCount.current < MAX_RETRIES) {
        retryCount.current++;
        console.log(
          `Retrying Highcharts initialization (attempt ${retryCount.current}/${MAX_RETRIES})...`,
        );
        setTimeout(() => {
          initializeHighcharts();
        }, 1000 * retryCount.current);
      }
    }
  }, []);

  const retry = useCallback(async () => {
    if (isRetrying) return;

    setIsRetrying(true);
    retryCount.current = 0;

    highchartsModule = null;

    const module = await getHighchartsModule();
    module.resetHighchartsInit();

    try {
      await initializeHighcharts();
    } finally {
      setIsRetrying(false);
    }
  }, [initializeHighcharts, isRetrying]);

  useEffect(() => {
    if (user && initAttempted.current) return;

    if (user) {
      initAttempted.current = true;
      initializeHighcharts();
    }
  }, [initializeHighcharts, user]);

  return (
    <HighchartsContext.Provider
      value={{
        isReady,
        error,
        retry,
        isRetrying,
        Highcharts: highchartsInstance,
      }}
    >
      {children}
    </HighchartsContext.Provider>
  );
}

export default HighchartsProvider;
